import { useEffect, useState } from 'react';
import { Container } from '@material-ui/core';
import { Button, Card } from '#root/components/ui';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import TripDTO from '#root/models/TripDTO';
import { tripService } from '#root/services';
import { useNotification, useDialog, usePaginatedListWithOffset } from '#root/hooks';
import { GuveryPageHeader } from '#root/ui-components';
import { PlusIcon, PlanenIcon } from '#root/icons';
import { IconRefresh, IconCheck } from '@tabler/icons';
import { GlobeTrip } from '#root/assets';
import * as ROUTES from '#root/constants/navigation';
import TripCard from './TripCard';
import UpdateDialog from './UpdateDialog';
import styles from './travelerTrips.module.scss';

const TravelerTrips = () => {
  const updateDialog = useDialog();
  const { createMessage } = useNotification();
  const [trips, setTrips] = useState<TripDTO[]>([]);
  const [tripToEdit, setTripToEdit] = useState<TripDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingPagination, setLoadingPagination] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [paginable, setPaginable] = useState(true);
  const ITEMS_BY_PAGE = 7;

  // Hook de paginación con IntersectionObserver
  const { lastElementRef: lastTripElementRef } = usePaginatedListWithOffset(
    loading,
    hasMore,
    offset,
    setOffset,
    ITEMS_BY_PAGE,
    { enabled: paginable }
  );

  const fetchTripsList = async () => {
    // Si offset > 0, mostrar loading de paginación
    if (offset > 0) setLoadingPagination(true);

    setLoading(true);
    setPaginable(true);

    const res = await tripService.list(offset);

    setLoadingPagination(false);
    setLoading(false);

    if (res.error) return;

    // Si offset > 0, APPEND items (paginación)
    if (offset > 0) {
      setTrips((prevTrips) => [...prevTrips, ...res.data]);
    } else {
      // Si offset === 0, REPLACE items (fetch inicial)
      setTrips(res.data);
    }

    // Calcular si hay más items
    setHasMore(res.data.length > ITEMS_BY_PAGE - 1);
  };

  useEffect(() => {
    fetchTripsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect para paginación
  useEffect(() => {
    // Solo fetch cuando offset > 0 (paginación)
    if (offset > 0) {
      fetchTripsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const handleEditTrip = (trip: TripDTO) => {
    setTripToEdit(trip);
    updateDialog.openDialog();
  };

  // El PUT devuelve el viaje fresco (con is_offerable y edit_policy): se hace
  // merge en sitio en vez de refetch, porque `fetchTripsList` APPENDEA cuando
  // `offset > 0` y duplicaria las tarjetas ya paginadas. El spread conserva los
  // counts (`offers_count`, `deals_count`, `deals_paid_count`), que el PUT no trae.
  const handleUpdateSuccess = (updatedTrip: TripDTO) => {
    if (!updatedTrip?.id) return;
    setTrips((prevTrips) =>
      prevTrips.map((item) =>
        item.id === updatedTrip.id ? { ...item, ...updatedTrip } : item,
      ),
    );
    setTripToEdit(updatedTrip);
  };

  const content = (
    <>
      {trips.length !== 0 ? (
        <div className={styles.stack}>
          {trips.map((item, index) => {
            // Asignar ref al ÚLTIMO elemento
            if (trips.length === index + 1) {
              return (
                <TripCard
                  key={index}
                  trip={item}
                  xref={lastTripElementRef}
                  createMessage={createMessage}
                  fetchTrips={fetchTripsList}
                  openDialog={() => handleEditTrip(item)}
                />
              );
            } else {
              return (
                <TripCard
                  key={index}
                  trip={item}
                  createMessage={createMessage}
                  fetchTrips={fetchTripsList}
                  openDialog={() => handleEditTrip(item)}
                />
              );
            }
          })}

          {/* Loading indicator durante paginación */}
          {paginable && loadingPagination && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <h3 className="guv-text-alt-2">
                <IconRefresh size={18} />
                {' '}
                Cargando más viajes...
              </h3>
            </div>
          )}

          {/* Mensaje de fin de lista */}
          {paginable && !loadingPagination && !hasMore && trips.length >= ITEMS_BY_PAGE && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <h3 className="guv-text-alt-2">
                <IconCheck size={18} />
                {' '}
                Has revisado todos tus viajes
              </h3>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.noTrip}>
          <img src={GlobeTrip} alt="globe trip" />
          <h5>
            Crea tu primer viaje para poder hacer ofertas sobre los pedidos disponibles.
            Recuerda que sólo debes crear un viaje cuando tengas los pasajes comprados y las
            fechas confirmadas.
          </h5>
          <Button
            variant="primary"
            icon={<PlanenIcon />}
            size="lg"
            to={ROUTES.TRAVELER_TRIPS_NEW}>
            Crear mi viaje
          </Button>
        </div>
      )}
    </>
  );

  const btnNewTrip = (
    <Button
      variant="outline"
      icon={<PlusIcon />}
      to={ROUTES.TRAVELER_TRIPS_NEW}>
      Nuevo Viaje
    </Button>
  );

  return (
    <div className={clsx(styles.root, 'guv-main-container')}>
      <Container maxWidth="lg">
        <GuveryPageHeader title="Mis Viajes" extra={btnNewTrip} />
        {!loading || loadingPagination ? (
          <>{content}</>
        ) : (
          <div className={styles.stack}>
            {Array.from(new Array(8)).map((_item, key) => (
              <Card noHover key={key}>
                <Skeleton variant="rect" height={245} />
              </Card>
            ))}
          </div>
        )}
      </Container>
      <UpdateDialog
        open={updateDialog.open}
        trip={tripToEdit}
        onClose={updateDialog.closeDialog}
        onSuccess={handleUpdateSuccess}
      />
    </div>
  );
};

export default TravelerTrips;
